import { BaseGenerator } from './BaseGenerator.js';
import { ZONE } from './ZoneTypes.js';
import { MapPipeline } from './MapPipeline.js';

/**
 * Ruin map generator.
 * Produces crumbling structures overgrown with vegetation,
 * featuring partial walls, broken floors, and scattered debris.
 *
 * Supports three quality modes:
 * - "draft"    — direct tile ID output (legacy, fast)
 * - "standard" — pipeline with transition + cleanup passes
 * - "high"     — pipeline + decoration (vines, rubble, overgrowth)
 */
export class RuinGenerator extends BaseGenerator {
  generate(content) {
    const quality = this.options.quality || 'draft';

    if (quality === 'draft') {
      return this._generateDraft(content);
    }

    return this._generatePipeline(content, quality);
  }

  // ---- Pipeline mode (standard / high) ----

  _generatePipeline(content, quality) {
    const tiles = content.tiles || RuinGenerator.DEFAULT_TILES;
    const opts = { ...RuinGenerator.DEFAULT_OPTIONS, ...this.options };

    // 1. Structure pass — building footprints with decay
    const ground = this.createGrid(this.width, this.height, ZONE.GRASS);
    const objects = this.createGrid(this.width, this.height, ZONE.EMPTY);
    const overhead = this.createGrid(this.width, this.height, ZONE.EMPTY);

    // Generate building footprints
    const buildings = this._generateBuildings(opts);

    // Lay down floors
    for (const b of buildings) {
      for (let y = b.y; y < b.y + b.h; y++) {
        for (let x = b.x; x < b.x + b.w; x++) {
          this.setCell(ground, x, y, ZONE.FLOOR);
        }
      }
    }

    // Decayed walls (with random gaps)
    this._placeDecayedWallsZone(objects, buildings, opts);

    // Break up floors
    this._decayFloorsZone(ground, buildings, opts);

    // Trails between ruins
    if (buildings.length > 1) {
      this._connectWithTrailsZone(ground, buildings);
    }

    // 2. Run pipeline
    const pipeline = MapPipeline.createDefault(this.rng, { tileMeta: this.options.tileMeta });

    if (quality !== 'high') {
      pipeline.removePass('decoration');
    }

    const pipelineOptions = {
      tileMapping: this._buildTileMapping(tiles),
      floorTileIds: [ZONE.GRASS, ZONE.FLOOR, ZONE.FLOOR_CRACKED, ZONE.PATH],
      corridorTileId: ZONE.PATH,
    };

    if (quality === 'high') {
      pipelineOptions.decorations = this._buildDecorationSpecs(tiles, opts);
      pipelineOptions.edgeDecorations = [
        {
          edgeTileId: ZONE.FLOOR,
          decorations: [
            { tileId: ZONE.VINE, chance: 0.1, spacing: 3 },
            { tileId: ZONE.BUSH, chance: 0.08, spacing: 4 },
            { tileId: ZONE.RUBBLE, chance: 0.12, spacing: 3 },
          ],
        },
      ];
    }

    const result = pipeline.run({
      ground,
      objects,
      overhead,
      zones: ground,
      width: this.width,
      height: this.height,
      metadata: { buildings },
    }, pipelineOptions);

    // 3. Resolve zones to tile IDs
    const finalGround = this._resolveZones(result.ground, tiles);
    const finalObjects = this._resolveObjectZones(result.objects, tiles);
    const finalOverhead = this._resolveObjectZones(result.overhead, tiles);

    // High-quality overgrowth: trees growing through ruins
    if (quality === 'high' && tiles.tree) {
      this._addOvergrowthTrees(finalObjects, finalGround, finalOverhead, tiles, opts, buildings);
    }

    if (tiles._tileLayers) {
      this.redistributeByLayer(
        { ground: finalGround, objects: finalObjects, overhead: finalOverhead },
        tiles._tileLayers,
      );
    }

    return this.buildTMJ({
      layers: [
        { name: 'Ground', data: this.flattenGrid(finalGround) },
        { name: 'Objects', data: this.flattenGrid(finalObjects) },
        { name: 'Overhead', data: this.flattenGrid(finalOverhead) },
      ],
      tilesets: content.tilesets || [],
    });
  }

  _placeDecayedWallsZone(objects, buildings, opts) {
    const decayChance = opts.wallDecay || 0.4;

    for (const b of buildings) {
      for (let x = b.x; x < b.x + b.w; x++) {
        if (!this.rng.chance(decayChance)) this.setCell(objects, x, b.y, ZONE.WALL);
        if (!this.rng.chance(decayChance)) this.setCell(objects, x, b.y + b.h - 1, ZONE.WALL);
      }
      for (let y = b.y; y < b.y + b.h; y++) {
        if (!this.rng.chance(decayChance)) this.setCell(objects, b.x, y, ZONE.WALL);
        if (!this.rng.chance(decayChance)) this.setCell(objects, b.x + b.w - 1, y, ZONE.WALL);
      }

      // Crumbled wall variants
      for (let y = b.y; y < b.y + b.h; y++) {
        for (let x = b.x; x < b.x + b.w; x++) {
          if (this.getCell(objects, x, y) === ZONE.WALL && this.rng.chance(0.25)) {
            this.setCell(objects, x, y, ZONE.WALL_CRUMBLED);
          }
        }
      }
    }
  }

  _decayFloorsZone(ground, buildings, opts) {
    const floorDecay = opts.floorDecay || 0.2;

    for (const b of buildings) {
      for (let y = b.y; y < b.y + b.h; y++) {
        for (let x = b.x; x < b.x + b.w; x++) {
          if (this.getCell(ground, x, y) === ZONE.FLOOR && this.rng.chance(floorDecay)) {
            this.setCell(ground, x, y, this.rng.chance(0.5) ? ZONE.FLOOR_CRACKED : ZONE.GRASS);
          }
        }
      }
    }
  }

  _connectWithTrailsZone(ground, buildings) {
    for (let i = 0; i < buildings.length - 1; i++) {
      const a = buildings[i];
      const b = buildings[i + 1];
      const ax = Math.floor(a.x + a.w / 2);
      const ay = Math.floor(a.y + a.h / 2);
      const bx = Math.floor(b.x + b.w / 2);
      const by = Math.floor(b.y + b.h / 2);

      let cx = ax, cy = ay;
      for (let step = 0; step < (this.width + this.height) * 2; step++) {
        if (this.getCell(ground, cx, cy) === ZONE.GRASS) {
          this.setCell(ground, cx, cy, ZONE.PATH);
          if (cx + 1 < this.width) this.setCell(ground, cx + 1, cy, ZONE.PATH);
        }
        if (cx === bx && cy === by) break;
        if (this.rng.chance(0.65)) {
          if (cx !== bx) cx += cx < bx ? 1 : -1;
          else if (cy !== by) cy += cy < by ? 1 : -1;
        } else {
          cx += this.rng.nextInt(-1, 1);
          cy += this.rng.nextInt(-1, 1);
        }
        cx = Math.max(0, Math.min(this.width - 1, cx));
        cy = Math.max(0, Math.min(this.height - 1, cy));
      }
    }
  }

  _addOvergrowthTrees(objects, ground, overhead, tiles, opts, buildings) {
    const density = opts.overgrowthDensity || 0.08;

    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (!this.rng.chance(density * 0.15)) continue;
        if (objects[y][x] !== 0) continue;

        // Only place trees on grass or floor near buildings
        const gTile = ground[y][x];
        if (gTile !== tiles.grass && gTile !== tiles.floor) continue;

        let nearBuilding = false;
        for (const b of buildings) {
          if (x >= b.x - 2 && x <= b.x + b.w + 1 && y >= b.y - 2 && y <= b.y + b.h + 1) {
            nearBuilding = true;
            break;
          }
        }
        if (!nearBuilding) continue;

        objects[y][x] = tiles.tree;
        if (tiles.treeTop && y > 0) {
          overhead[y - 1][x] = tiles.treeTop;
        }
      }
    }
  }

  _buildTileMapping(tiles) {
    return {
      [ZONE.GRASS]:         { center: tiles.grass },
      [ZONE.FLOOR]:         { center: tiles.floor },
      [ZONE.FLOOR_CRACKED]: { center: tiles.floorCracked || tiles.floor },
      [ZONE.PATH]:          { center: tiles.path || tiles.floor },
    };
  }

  _buildDecorationSpecs(tiles, opts) {
    const specs = [];
    if (tiles.rubble) {
      specs.push({
        tileId: ZONE.RUBBLE,
        density: 0.015,
        spacing: 3,
        validGround: [ZONE.FLOOR, ZONE.FLOOR_CRACKED, ZONE.GRASS],
        avoidEdges: true,
      });
    }
    if (tiles.vine) {
      specs.push({
        tileId: ZONE.VINE,
        density: 0.01,
        spacing: 3,
        validGround: [ZONE.FLOOR, ZONE.GRASS],
        avoidEdges: false,
      });
    }
    if (tiles.bush) {
      specs.push({
        tileId: ZONE.BUSH,
        density: 0.012,
        spacing: 4,
        validGround: [ZONE.GRASS, ZONE.FLOOR_CRACKED],
        avoidEdges: false,
      });
    }
    return specs;
  }

  _resolveZones(grid, tiles) {
    const mapping = {
      [ZONE.GRASS]:         tiles.grass,
      [ZONE.FLOOR]:         tiles.floor,
      [ZONE.FLOOR_CRACKED]: tiles.floorCracked || tiles.floor,
      [ZONE.PATH]:          tiles.path || tiles.floor,
    };
    return grid.map(row => row.map(z => mapping[z] !== undefined ? mapping[z] : z));
  }

  _resolveObjectZones(grid, tiles) {
    const rubbleTiles = Array.isArray(tiles.rubble) ? tiles.rubble : (tiles.rubble ? [tiles.rubble] : [0]);
    return grid.map(row => row.map(z => {
      if (z === ZONE.EMPTY || z === 0) return 0;
      if (z === ZONE.WALL) return tiles.wall;
      if (z === ZONE.WALL_CRUMBLED) return tiles.wallCrumbled || tiles.wall;
      if (z === ZONE.VINE) return tiles.vine || 0;
      if (z === ZONE.BUSH) return tiles.bush || 0;
      if (z === ZONE.RUBBLE) return this.rng.pick(rubbleTiles);
      if (z === ZONE.TREE) return tiles.tree || 0;
      return z;
    }));
  }

  // ---- Draft mode (original direct tile output) ----

  _generateDraft(content) {
    const tiles = content.tiles || RuinGenerator.DEFAULT_TILES;
    const opts = { ...RuinGenerator.DEFAULT_OPTIONS, ...this.options };

    const ground = this.createGrid(this.width, this.height, tiles.grass);
    const objects = this.createGrid(this.width, this.height, 0);
    const overhead = this.createGrid(this.width, this.height, 0);

    // 1. Generate original building footprints
    const buildings = this._generateBuildings(opts);

    // 2. Lay down floors
    for (const b of buildings) {
      for (let y = b.y; y < b.y + b.h; y++) {
        for (let x = b.x; x < b.x + b.w; x++) {
          this.setCell(ground, x, y, tiles.floor);
        }
      }
    }

    // 3. Place walls with decay
    this._placeDecayedWalls(objects, buildings, tiles, opts);

    // 4. Break up floors
    this._decayFloors(ground, buildings, tiles, opts);

    // 5. Overgrowth
    this._addOvergrowth(ground, objects, overhead, tiles, opts);

    // 6. Rubble and debris
    this._addDebris(objects, ground, buildings, tiles, opts);

    // 7. Paths between ruins
    if (buildings.length > 1) {
      this._connectWithTrails(ground, buildings, tiles);
    }

    if (tiles._tileLayers) {
      this.redistributeByLayer(
        { ground, objects, overhead },
        tiles._tileLayers,
      );
    }

    return this.buildTMJ({
      layers: [
        { name: 'Ground', data: this.flattenGrid(ground) },
        { name: 'Objects', data: this.flattenGrid(objects) },
        { name: 'Overhead', data: this.flattenGrid(overhead) },
      ],
      tilesets: content.tilesets || [],
    });
  }

  // ---- Shared helpers ----

  _generateBuildings(opts) {
    const buildings = [];
    const count = opts.buildingCount || this.rng.nextInt(3, 7);

    for (let i = 0; i < count * 4 && buildings.length < count; i++) {
      const w = this.rng.nextInt(opts.minBuildingSize, opts.maxBuildingSize);
      const h = this.rng.nextInt(opts.minBuildingSize, opts.maxBuildingSize);
      const x = this.rng.nextInt(2, this.width - w - 2);
      const y = this.rng.nextInt(2, this.height - h - 2);

      let overlap = false;
      for (const b of buildings) {
        if (x < b.x + b.w + 2 && x + w + 2 > b.x &&
            y < b.y + b.h + 2 && y + h + 2 > b.y) {
          overlap = true;
          break;
        }
      }

      if (!overlap) {
        buildings.push({ x, y, w, h });
      }
    }

    return buildings;
  }

  // ---- Original helpers (used by draft mode) ----

  _placeDecayedWalls(objects, buildings, tiles, opts) {
    const decayChance = opts.wallDecay || 0.4;

    for (const b of buildings) {
      for (let x = b.x; x < b.x + b.w; x++) {
        if (!this.rng.chance(decayChance)) this.setCell(objects, x, b.y, tiles.wall);
        if (!this.rng.chance(decayChance)) this.setCell(objects, x, b.y + b.h - 1, tiles.wall);
      }
      for (let y = b.y; y < b.y + b.h; y++) {
        if (!this.rng.chance(decayChance)) this.setCell(objects, b.x, y, tiles.wall);
        if (!this.rng.chance(decayChance)) this.setCell(objects, b.x + b.w - 1, y, tiles.wall);
      }

      if (tiles.wallCrumbled) {
        for (let y = b.y; y < b.y + b.h; y++) {
          for (let x = b.x; x < b.x + b.w; x++) {
            if (objects[y]?.[x] === tiles.wall && this.rng.chance(0.25)) {
              this.setCell(objects, x, y, tiles.wallCrumbled);
            }
          }
        }
      }
    }
  }

  _decayFloors(ground, buildings, tiles, opts) {
    const floorDecay = opts.floorDecay || 0.2;

    for (const b of buildings) {
      for (let y = b.y; y < b.y + b.h; y++) {
        for (let x = b.x; x < b.x + b.w; x++) {
          if (ground[y]?.[x] === tiles.floor && this.rng.chance(floorDecay)) {
            this.setCell(ground, x, y, this.rng.chance(0.5) ? (tiles.floorCracked || tiles.floor) : tiles.grass);
          }
        }
      }
    }
  }

  _addOvergrowth(ground, objects, overhead, tiles, opts) {
    const overgrowthDensity = opts.overgrowthDensity || 0.08;

    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (!this.rng.chance(overgrowthDensity)) continue;

        if (ground[y][x] === tiles.grass || ground[y][x] === tiles.floor) {
          if (objects[y][x] === 0) {
            if (this.rng.chance(0.3) && tiles.vine) {
              this.setCell(objects, x, y, tiles.vine);
            } else if (this.rng.chance(0.3) && tiles.bush) {
              this.setCell(objects, x, y, tiles.bush);
            } else if (this.rng.chance(0.15) && tiles.tree) {
              this.setCell(objects, x, y, tiles.tree);
              if (tiles.treeTop && y > 0) {
                this.setCell(overhead, x, y - 1, tiles.treeTop);
              }
            }
          }
        }
      }
    }
  }

  _addDebris(objects, ground, buildings, tiles, opts) {
    if (!tiles.rubble) return;
    const rubbleTiles = Array.isArray(tiles.rubble) ? tiles.rubble : [tiles.rubble];

    for (const b of buildings) {
      const debrisCount = this.rng.nextInt(3, 8);
      for (let i = 0; i < debrisCount; i++) {
        const x = b.x + this.rng.nextInt(-2, b.w + 1);
        const y = b.y + this.rng.nextInt(-2, b.h + 1);
        if (this.getCell(objects, x, y) === 0) {
          this.setCell(objects, x, y, this.rng.pick(rubbleTiles));
        }
      }
    }
  }

  _connectWithTrails(ground, buildings, tiles) {
    for (let i = 0; i < buildings.length - 1; i++) {
      const a = buildings[i];
      const b = buildings[i + 1];
      const ax = Math.floor(a.x + a.w / 2);
      const ay = Math.floor(a.y + a.h / 2);
      const bx = Math.floor(b.x + b.w / 2);
      const by = Math.floor(b.y + b.h / 2);

      let cx = ax, cy = ay;
      for (let step = 0; step < (this.width + this.height) * 2; step++) {
        if (ground[cy]?.[cx] === tiles.grass) {
          this.setCell(ground, cx, cy, tiles.path || tiles.floor);
        }
        if (cx === bx && cy === by) break;
        if (this.rng.chance(0.65)) {
          if (cx !== bx) cx += cx < bx ? 1 : -1;
          else if (cy !== by) cy += cy < by ? 1 : -1;
        } else {
          cx += this.rng.nextInt(-1, 1);
          cy += this.rng.nextInt(-1, 1);
        }
        cx = Math.max(0, Math.min(this.width - 1, cx));
        cy = Math.max(0, Math.min(this.height - 1, cy));
      }
    }
  }

  static DEFAULT_TILES = {
    grass: 1,
    floor: 3,
    floorCracked: 4,
    path: 5,
    wall: 10,
    wallCrumbled: 11,
    vine: 20,
    bush: 21,
    tree: 30,
    treeTop: 31,
    rubble: [40, 41, 42],
  };

  static DEFAULT_OPTIONS = {
    buildingCount: 5,
    minBuildingSize: 4,
    maxBuildingSize: 8,
    wallDecay: 0.35,
    floorDecay: 0.2,
    overgrowthDensity: 0.08,
  };

  static getEmittedZones() {
    return [
      ZONE.GRASS, ZONE.PATH, ZONE.FLOOR, ZONE.FLOOR_CRACKED, ZONE.WALL, ZONE.WALL_CRUMBLED, ZONE.RUBBLE, ZONE.VINE, ZONE.TREE, ZONE.BUSH,
    ];
  }
}
